/**
 * Emoji presets — reusable, user-owned ordered emoji sequences.
 *
 * A preset is a TEMPLATE: applying it writes emoji onto stickers, but editing
 * the preset later never rewrites already-assigned stickers. Presets are
 * persisted server-side (per user), not in UI local storage.
 */
import { z } from 'zod';
import { isValidEmoji, MAX_PRESET_EMOJIS } from './sticker.js';

/** Public shape of an emoji preset returned by the API. */
export interface EmojiPresetDTO {
  id: string;
  ownerId: string;
  name: string;
  /** Ordered emoji sequence (1..MAX_PRESET_EMOJIS). */
  emojis: string[];
  createdAt: string;
  updatedAt: string;
}

/** A single emoji token, validated against Telegram-safe emoji rules. */
const emojiToken = z.string().refine((v) => isValidEmoji(v), {
  message: 'Each entry must be a valid emoji.',
});

const emojiSequence = z.array(emojiToken).min(1).max(MAX_PRESET_EMOJIS);

const presetName = z.string().trim().min(1).max(40);

export const createEmojiPresetSchema = z
  .object({
    name: presetName,
    emojis: emojiSequence,
  })
  .strict();

export type CreateEmojiPresetInput = z.infer<typeof createEmojiPresetSchema>;

export const updateEmojiPresetSchema = z
  .object({
    name: presetName.optional(),
    emojis: emojiSequence.optional(),
  })
  .strict()
  .refine((v) => v.name !== undefined || v.emojis !== undefined, {
    message: 'Provide a name and/or emojis to update.',
  });

export type UpdateEmojiPresetInput = z.infer<typeof updateEmojiPresetSchema>;
